import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import type { FinanceCashForecast } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

const HORIZONS = [7, 30, 60, 90, 365];

function fmt(n?: number) {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function FinanceCashForecastPage() {
  const [days, setDays] = useState(30);
  const [forecast, setForecast] = useState<FinanceCashForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const buckets = forecast?.buckets ?? [];
  const { rows: paginatedBuckets, tablePaginationProps: bucketsPaginationProps } = useClientPaginatedRows(buckets);

  const load = (horizon: number) => {
    setLoading(true);
    financeApi
      .platformCashForecast(horizon)
      .then(setForecast)
      .catch((e) => showApiError(e, 'Failed to load cash forecast'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
  }, [days]);

  return (
    <div>
      <PageHeader
        title="Cash flow forecast"
        description="Expected cash in and out by period — based on open receivables and payables due dates"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Treasury', to: '/finance/treasury' },
          { label: 'Forecast' },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            {HORIZONS.map((h) => (
              <Button key={h} variant={h === days ? 'primary' : 'secondary'} size="sm" onClick={() => setDays(h)}>
                {h === 365 ? '1 year' : `${h} days`}
              </Button>
            ))}
          </div>
        }
      />

      {forecast?.shortageAlert && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          Cash shortage alert — projected closing cash is negative. Review payables and collections.
        </div>
      )}

      {!loading && forecast && (
        <>
          <KpiGrid className="mb-6">
            <StatCard title="Opening cash" value={fmt(forecast.openingCash)} />
            <StatCard title="Expected inflows" value={fmt(forecast.totalExpectedInflows)} />
            <StatCard title="Expected outflows" value={fmt(forecast.totalExpectedOutflows)} />
            <StatCard title="Projected closing" value={fmt(forecast.projectedClosingCash)} />
          </KpiGrid>

          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-3">Period</th>
                  <th className="p-3 text-right">Inflows</th>
                  <th className="p-3 text-right">Outflows</th>
                  <th className="p-3 text-right">Net</th>
                  <th className="p-3 text-right">Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBuckets.map((b) => (
                  <tr key={b.label} className="border-t">
                    <td className="p-3">{b.label}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(b.expectedInflows)}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(b.expectedOutflows)}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(b.netCash)}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{fmt(b.cumulativeCash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination {...bucketsPaginationProps} hasRows={buckets.length > 0} loading={loading} />
          </div>
        </>
      )}

      <p className="mt-4 text-xs text-slate-500">
        <Link to="/finance/treasury" className="text-primary-600 hover:underline">
          ← Back to treasury
        </Link>
      </p>
    </div>
  );
}
